// GraphQL Queries and Mutations for commit.you

// ============================================================================
// User Queries
// ============================================================================

export const GET_USER = /* GraphQL */ `
  query GetUser($username: String!) {
    getUser(username: $username) {
      id
      username
      displayName
      avatarUrl
      bio
      location
      githubUrl
      linkedinUrl
      twitterUrl
      blueskyUrl
      portfolioUrl
      badges {
        type
        awardedAt
      }
      followerCount
      followingCount
      commitCount
      branches {
        branch
        commitCount
        sortOrder
        commitSortDirection
        createdAt
        updatedAt
      }
      isFollowing
      createdAt
      updatedAt
    }
  }
`;

export const GET_ME = /* GraphQL */ `
  query GetMe {
    getMe {
      id
      username
      displayName
      avatarUrl
      bio
      location
      githubUrl
      linkedinUrl
      twitterUrl
      blueskyUrl
      portfolioUrl
      badges {
        type
        awardedAt
      }
      followerCount
      followingCount
      commitCount
      branches {
        branch
        commitCount
        sortOrder
        commitSortDirection
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`;

// ============================================================================
// Commit Queries
// ============================================================================

export const GET_COMMIT = /* GraphQL */ `
  query GetCommit($id: ID!) {
    getCommit(id: $id) {
      id
      userId
      user {
        id
        username
        displayName
        avatarUrl
        badges {
          type
          awardedAt
        }
      }
      type
      branch
      message
      body
      takeaways {
        items {
          type
          text
        }
      }
      snippet {
        before
        after
        language
      }
      tags
      mentions {
        userId
        user {
          id
          username
          displayName
        }
        position
      }
      reactionCounts {
        heart
        celebration
        fire
        sleep
        dynamite
      }
      userReactions
      commentCount
      createdAt
      updatedAt
    }
  }
`;

export const GET_USER_COMMITS = /* GraphQL */ `
  query GetUserCommits($userId: ID!, $limit: Int, $nextToken: String, $sortDirection: String, $branch: String) {
    getUserCommits(userId: $userId, limit: $limit, nextToken: $nextToken, sortDirection: $sortDirection, branch: $branch) {
      items {
        id
        userId
        user {
          id
          username
          displayName
          avatarUrl
          badges {
            type
            awardedAt
          }
        }
        type
        branch
        message
        body
        takeaways {
          items {
            type
            text
          }
        }
        snippet {
          before
          after
          language
        }
        tags
        reactionCounts {
          heart
          celebration
          fire
          sleep
          dynamite
        }
        userReactions
        commentCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_FEED = /* GraphQL */ `
  query GetFeed($limit: Int, $nextToken: String) {
    getFeed(limit: $limit, nextToken: $nextToken) {
      items {
        id
        userId
        user {
          id
          username
          displayName
          avatarUrl
          badges {
            type
            awardedAt
          }
        }
        type
        branch
        message
        body
        takeaways {
          items {
            type
            text
          }
        }
        snippet {
          before
          after
          language
        }
        tags
        reactionCounts {
          heart
          celebration
          fire
          sleep
          dynamite
        }
        userReactions
        commentCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_PUBLIC_FEED = /* GraphQL */ `
  query GetPublicFeed($limit: Int, $nextToken: String) {
    getPublicFeed(limit: $limit, nextToken: $nextToken) {
      items {
        id
        userId
        user {
          id
          username
          displayName
          avatarUrl
          badges {
            type
            awardedAt
          }
        }
        type
        branch
        message
        body
        takeaways {
          items {
            type
            text
          }
        }
        snippet {
          before
          after
          language
        }
        tags
        reactionCounts {
          heart
          celebration
          fire
          sleep
          dynamite
        }
        userReactions
        commentCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_EXPLORE_FEED = /* GraphQL */ `
  query GetExploreFeed($limit: Int, $nextToken: String) {
    getExploreFeed(limit: $limit, nextToken: $nextToken) {
      items {
        id
        userId
        user {
          id
          username
          displayName
          avatarUrl
          badges {
            type
            awardedAt
          }
        }
        type
        branch
        message
        body
        takeaways {
          items {
            type
            text
          }
        }
        snippet {
          before
          after
          language
        }
        tags
        reactionCounts {
          heart
          celebration
          fire
          sleep
          dynamite
        }
        userReactions
        commentCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_COMMITS_BY_TAG = /* GraphQL */ `
  query GetCommitsByTag($tag: String!, $limit: Int, $nextToken: String) {
    getCommitsByTag(tag: $tag, limit: $limit, nextToken: $nextToken) {
      items {
        id
        userId
        user {
          id
          username
          displayName
          avatarUrl
          badges {
            type
            awardedAt
          }
        }
        type
        branch
        message
        body
        takeaways {
          items {
            type
            text
          }
        }
        snippet {
          before
          after
          language
        }
        tags
        reactionCounts {
          heart
          celebration
          fire
          sleep
          dynamite
        }
        userReactions
        commentCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ============================================================================
// Commit Mutations
// ============================================================================

export const CREATE_COMMIT = /* GraphQL */ `
  mutation CreateCommit($input: CreateCommitInput!) {
    createCommit(input: $input) {
      id
      userId
      type
      branch
      message
      body
      snippet {
        before
        after
        language
      }
      tags
      mentions {
        userId
        position
      }
      reactionCounts {
        heart
        celebration
        fire
        sleep
        dynamite
      }
      commentCount
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_COMMIT = /* GraphQL */ `
  mutation UpdateCommit($id: ID!, $input: UpdateCommitInput!) {
    updateCommit(id: $id, input: $input) {
      id
      type
      message
      body
      snippet {
        before
        after
        language
      }
      tags
      updatedAt
    }
  }
`;

export const DELETE_COMMIT = /* GraphQL */ `
  mutation DeleteCommit($id: ID!) {
    deleteCommit(id: $id)
  }
`;

// ============================================================================
// Reaction Mutations
// ============================================================================

export const ADD_REACTION = /* GraphQL */ `
  mutation AddReaction($commitId: ID!, $type: ReactionType!) {
    addReaction(commitId: $commitId, type: $type) {
      id
      commitId
      userId
      type
      createdAt
    }
  }
`;

export const REMOVE_REACTION = /* GraphQL */ `
  mutation RemoveReaction($commitId: ID!, $type: ReactionType!) {
    removeReaction(commitId: $commitId, type: $type)
  }
`;

// ============================================================================
// Comment Operations
// ============================================================================

export const GET_COMMENTS = /* GraphQL */ `
  query GetComments($commitId: ID!, $limit: Int, $nextToken: String) {
    getComments(commitId: $commitId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        commitId
        userId
        user {
          id
          username
          displayName
          avatarUrl
          badges {
            type
            awardedAt
          }
        }
        parentId
        body
        replyCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const CREATE_COMMENT = /* GraphQL */ `
  mutation CreateComment($commitId: ID!, $body: String!, $parentId: ID) {
    createComment(commitId: $commitId, body: $body, parentId: $parentId) {
      id
      commitId
      userId
      user {
        id
        username
        displayName
        avatarUrl
        badges {
          type
          awardedAt
        }
      }
      parentId
      body
      replyCount
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_COMMENT = /* GraphQL */ `
  mutation DeleteComment($id: ID!) {
    deleteComment(id: $id)
  }
`;

// ============================================================================
// Social Operations
// ============================================================================

export const FOLLOW_USER = /* GraphQL */ `
  mutation FollowUser($userId: ID!) {
    followUser(userId: $userId) {
      followerId
      followingId
      createdAt
    }
  }
`;

export const UNFOLLOW_USER = /* GraphQL */ `
  mutation UnfollowUser($userId: ID!) {
    unfollowUser(userId: $userId)
  }
`;

// ============================================================================
// Profile Operations
// ============================================================================

export const UPDATE_PROFILE = /* GraphQL */ `
  mutation UpdateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      id
      username
      displayName
      avatarUrl
      bio
      location
      githubUrl
      linkedinUrl
      twitterUrl
      blueskyUrl
      portfolioUrl
      updatedAt
    }
  }
`;

// ============================================================================
// Avatar Operations
// ============================================================================

export const GET_AVATAR_UPLOAD_URL = /* GraphQL */ `
  mutation GetAvatarUploadUrl($contentType: String!) {
    getAvatarUploadUrl(contentType: $contentType) {
      uploadUrl
      avatarUrl
    }
  }
`;

// ============================================================================
// Username Operations
// ============================================================================

export const IS_USERNAME_AVAILABLE = /* GraphQL */ `
  query IsUsernameAvailable($username: String!) {
    isUsernameAvailable(username: $username)
  }
`;

// ============================================================================
// LinkedIn Operations
// ============================================================================

export const GENERATE_LINKEDIN_POST = /* GraphQL */ `
  mutation GenerateLinkedInPost($commitId: ID!) {
    generateLinkedInPost(commitId: $commitId) {
      title
      body
      url
    }
  }
`;

// ============================================================================
// Branch Operations
// ============================================================================

export const UPDATE_BRANCH_SETTINGS = /* GraphQL */ `
  mutation UpdateBranchSettings($input: UpdateBranchSettingsInput!) {
    updateBranchSettings(input: $input) {
      branch
      commitCount
      sortOrder
      commitSortDirection
      createdAt
      updatedAt
    }
  }
`;

export const REORDER_BRANCHES = /* GraphQL */ `
  mutation ReorderBranches($input: ReorderBranchesInput!) {
    reorderBranches(input: $input) {
      branch
      commitCount
      sortOrder
      commitSortDirection
      createdAt
      updatedAt
    }
  }
`;
